import type { AssortmentSimulationTranslations } from "../en/AssortmentSimulation";
import type { TranslationShape } from "../../store/types";

export const assortmentSimulationEs: TranslationShape<AssortmentSimulationTranslations> = {
  pageTitle: "Simulación de Surtido",
  pageSubtitle:
    "Edita y optimiza simulaciones con recomendaciones de IA para revisión mayor y menor, y genera planogramas",

  banner: {
    title: "Convierte Simulaciones en Decisiones Listas para el Anaquel",
    description:
      "Comienza revisando tus simulaciones pendientes, o consulta las ya utilizadas como referencia. Explora el impacto de cada una, haz clic en Ver/Editar para ver los detalles del surtido y ajustarlos, luego verifica cómo tus cambios afectan la utilización del planograma. Cuando estés listo, actualiza los planogramas en las tiendas que necesites.",
  },

  columns: {
    simulation: "Simulación",
    category: "Categoría",
    stores: "Tiendas",
    geoLevel: "Segmentación",
    impact: "Impacto",
    skuChanges: "Cambios de SKU",
    created: "Creado",
    createdBy: "Creado Por",
    status: "Estado",
    reviewType: "Tipo de Revisión",
    actions: "Acciones",
  },

  segmentationTypes: {
    General: "General",
    Cluster: "Cluster Institucional",
    Store: "Tienda",
    Unknown: "Desconocido",
    "Cluster Category": "Categoría de Cluster",
    "Cluster Institutional": "Cluster Institucional",
  },

  search: "Buscar simulaciones...",

  filters: {
    segmentation: "Segmentación",
    allSegmentations: "Todas las Segmentaciones",
    category: "Categoría",
    allCategories: "Todas las Categorías",
    store: "Tienda",
    allStores: "Todas las Tiendas",
    type: "Tipo",
    allTypes: "Todos los Tipos",
    major: "Mayor",
    minor: "Menor",
    status: "Estado",
    allStatuses: "Todos",
    activeFilters: "Filtros activos:",
    searchLabel: "Búsqueda",
    clearAll: "Limpiar todo",
  },

  status: {
    pending: "Pendiente",
    inProgress: "En curso",
    used: "Utilizado",
    failed: "Falló",
    unknown: "Desconocido",
  },

  simulation: {
    recommendation: "recomendación",
    recommendations: "recomendaciones",
    added: "Agregados:",
    removed: "Eliminados:",
    net: "Neto:",
    update: "Actualizar",
    viewEdit: "Ver / Editar",
  },

  results: {
    showing: "Mostrando {{start}}-{{end}} de {{total}} simulaciones",
  },

  pagination: {
    rowsPerPage: "Filas por página:",
  },

  noResults: "No se encontraron simulaciones",
  noResultsDesc: "Crea una simulación desde el Análisis de Desempeño para comenzar",
  loading: "Cargando simulaciones",
  loadError: "No se pudieron cargar las simulaciones",
  loadErrorDesc: "Actualiza la página o inténtalo de nuevo en un momento.",

  placementModal: {
    title: "Elegir Método de Colocación",
    subtitle: "Elige cómo quieres generar la disposición del planograma",
    aiTitle: "Colocación IA",
    aiDescription:
      "Permite a la IA generar automáticamente la colocación óptima de productos basada en datos de ventas, jerarquía visual y mejores prácticas de gestión de categorías",
    recommended: "Recomendado",
    infoTitle: "¿Qué sucede después?",
    infoDescription:
      "Selecciona tu método de colocación preferido para proceder al paso de generación del planograma.",
  },

  merchConfig: {
    title: "Configuración de Merchandising",
    subtitle: "Configura las reglas de merchandising para la actualización de planogramas",
    merchandisingLabel: "Merchandising",
    toggles: {
      breakCases:
        "¿Podemos romper cajas / bandejas si un producto de rango central no cabe de otra manera?",
      fillExtraSpace:
        "¿Podemos llenar espacio extra junto a cajas / bandejas con productos sueltos?",
      squeezePlacedProducts:
        "¿Podemos compactar los productos ya colocados para hacer espacio para productos nuevos?",
      leverageAlternative:
        "¿Podemos usar orientaciones alternativas para llenar espacio vacío en un estante?",
      verticalOverlap: "¿Pueden los productos sobresalir verticalmente de los estantes?",
    },
    facingsStrategy: {
      title: "Estrategia de Ajuste de Caras",
      rebalanceAll: "Rebalancear Siempre",
      rebalanceAllDesc:
        "Siempre rebalancear caras en todos los estantes, sin importar si se realizan operaciones de actualización",
      affectedOnly: "Estantes Afectados",
      affectedOnlyDesc:
        "Solo rebalancear caras en estantes afectados por las operaciones de actualización",
      emptySpaceOnly: "Espacio Vacío",
      emptySpaceOnlyDesc:
        "Solo agregar/rebalancear caras para llenar espacio vacío disponible sin eliminar caras existentes",
      none: "Ninguno",
      noneDesc: "No realizar ningún ajuste de caras",
    },
    adjustFacingsMode: {
      title: "Modo de Objetivo de Caras",
      exactMatch: "Coincidencia Exacta",
      exactMatchDesc:
        "Coincide exactamente con el número objetivo de caras y devuelve errores cuando no es posible.",
      bestFit: "Mejor Ajuste",
      bestFitDesc:
        "Se acerca lo más posible a las caras objetivo respetando las restricciones del anaquel.",
    },
    startUpdate: "Iniciar Actualización",
  },

  updateModal: {
    title: "Actualizar Planogramas",
    subtitle: "Los planogramas se actualizarán con los cambios de surtido actuales",
    preparing: "Preparando actualización...",
    preparingDesc: "Verificando planogramas existentes y preparando el proceso de actualización.",
    readyTitle: "Listo para Actualizar",
    readyDesc:
      "Se encontró un merchflow existente con {{count}} planogramas de tienda listos para actualizar.",
    linkedTitle: "Ya Vinculado",
    linkedDesc:
      "Esta simulación ya está vinculada a un merchflow con {{count}} planogramas de tienda. Puede re-aplicar la actualización.",
    createdTitle: "Merchflow Creado",
    createdDesc:
      "Se creó un nuevo merchflow con {{count}} planogramas de tienda a partir de datos PSA.",
    noPsaTitle: "Sin Datos PSA Disponibles",
    noPsaDesc:
      "No se han cargado datos PSA para esta categoría. Carga archivos PSA a través del pipeline ETL o el flujo de importación de OWA antes de ejecutar una actualización de revisión menor.",
    noPogYetTitle: "Aún no hay POG para esta categoría",
    noPogYetDesc:
      "Existen datos PSA para el alcance seleccionado pero aún no se ha generado un planograma (POG). Ejecuta el merchflow trigger (frogger) desde el flujo de Space Planning primero, luego regresa aquí para aplicar los cambios de la simulación.",
    noPogSnapshotTitle: "No hay un snapshot de planograma disponible",
    noPogSnapshotDesc:
      "Esta simulación no contiene el snapshot de planograma necesario para una actualización segura. Vuelve a analizar los planogramas actuales y genera una nueva simulación antes de intentarlo de nuevo.",
    invalidMasterDataTitle: "Los datos del planograma deben corregirse antes de aplicar",
    invalidMasterDataDesc:
      "No se puede actualizar ninguna tienda porque faltan dimensiones requeridas de productos o planogramas padre, o estas no son válidas. Corrige los datos maestros indicados y regenera los planogramas afectados antes de volver a intentarlo.",
    invalidMasterDataStoreDetail: "Tienda {{store}}: {{products}} {{remaining}}",
    additionalItems: "+{{count}} más",
    missingStoreCodesWarning:
      "{{count}} tienda(s) seleccionada(s) no tienen planograma para esta categoría y fueron omitidas: {{stores}}",
    blockedStoresWarning:
      "{{count}} tienda(s) excluida(s) por incompatibilidad de dimensiones. Corrige el fixture y regenera el planograma para desbloquearlas.",
    blockedStoresStores: "Tiendas afectadas: {{stores}}",
    blockedStoresCodes: "Productos afectados: {{codes}}",
    availableStoreCodesHint: "Hay PSA disponible para {{count}} tienda(s): {{stores}}",
    categoryMismatchTitle: "Categoría no encontrada en el catálogo del comercio",
    categoryMismatchIntro: "La categoría de analytics",
    categoryMismatchIntroTail:
      " no tiene fila correspondiente en la tabla de categorías del comercio.",
    categoryMismatchAction:
      "Crea la categoría (o un alias) en el catálogo del comercio antes de ejecutar una revisión menor.",
    applyingTitle: "Aplicando Actualización...",
    applyingDesc:
      "Aplicando recomendaciones de simulación a los planogramas de tienda. Esto puede tomar un momento.",
    errorTitle: "Actualización Fallida",
    errorDesc: "Ocurrió un error al preparar la actualización: {{message}}",
    unexpectedStatus: "Estado de actualización inesperado: {{status}}",
    unknownError: "Ocurrió un error desconocido.",
    cancel: "Cancelar",
    update: "Aplicar Actualización",
    close: "Cerrar",
    staleSnapshotTitle: "El snapshot del POG puede estar desactualizado",
    staleSnapshotDesc:
      "El POG ha sido actualizado para {{count}} tienda(s) desde el análisis. Las recomendaciones podrían no aplicarse correctamente. ¿Re-analizar?",
    ospDriftedTitle: "El estado del anaquel cambió desde el análisis",
    ospDriftedDesc:
      "Los planogramas padre de {{count}} tienda(s) fueron actualizados por otra aplicación desde que se generó esta simulación. Re-analiza para alinear las recomendaciones con el anaquel actual.",
    reanalyze: "Re-analizar",
    continueToSpacePlanning: "Continuar de Todas Formas",
    alreadyAppliedTitle: "Ya Aplicado",
    alreadyAppliedDesc:
      "Esta simulación ya fue aplicada. Visualiza el planograma resultante en Space Planning en lugar de volver a aplicar.",
    viewAppliedPlanogram: "Ver Planograma Aplicado",
    lineageStrandedTitle: "El linaje del planograma ya no está disponible",
    lineageStrandedDesc:
      "Ya no se puede resolver la cadena de planogramas de origen de esta simulación. Vuelve a analizar los planogramas actuales antes de aplicar de nuevo.",
    partialSuccessTitle: "Planogramas actualizados con exclusiones",
    partialSuccessDesc:
      "La actualización se creó para las tiendas y productos válidos, pero se omitieron los elementos que aparecen a continuación.",
    partialSuccessSkippedProducts: "{{count}} producto(s) omitido(s):",
    partialSuccessProductDetail: "{{code}} — {{reason}}",
    partialSuccessStaleStores:
      "Se omitieron {{count}} tienda(s) porque cambió el snapshot de su planograma:",
    skippedProductReasons: {
      unit_depth_invalid: "la profundidad de la unidad falta o no es válida",
      parent_osp_geometry_invalid:
        "las dimensiones del producto no caben en la geometría del anaquel padre",
    },
    viewSpacePlanning: "Ver Space Planning",
  },
};
